import React, { useState, useCallback } from 'react';
import { View, Text, Pressable, Modal, ScrollView, TextInput, ActivityIndicator } from 'react-native';
import { MaterialCommunityIcons, MaterialIcons } from '@expo/vector-icons';
import { XStack, YStack } from 'tamagui';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import apis from '@api/api';

interface UserData {
  _id: string;
  title: string;
  description?: string;
  type?: string;
  tags?: string[];
  fieldCount: number;
  createdAt: string;
}

interface UserDataSelectorProps {
  isVisible: boolean;
  onClose: () => void;
  onSelect: (userDataId: string) => void;
  flowId: string;
}

/**
 * UserDataSelector Modal Component
 * Allows users to browse and select from user_datas collection for dynamic schema linking
 */
const UserDataSelector: React.FC<UserDataSelectorProps> = ({
  isVisible,
  onClose,
  onSelect,
  flowId
}) => {
  const theme = themeSelector();
  const [searchQuery, setSearchQuery] = useState('');
  const [userDatas, setUserDatas] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(false);

  const colors = {
    background: theme ? '#ffffff' : '#1e293b',
    surface: theme ? '#f8f9fa' : '#2d2d2d',
    border: theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
    text: theme ? '#333333' : '#e0e0e0',
    secondaryText: theme ? '#64748b' : '#94a3b8',
    accent: theme ? '#7c3aed' : '#a78bfa'
  };

  // Fetch user_datas when modal opens
  React.useEffect(() => {
    if (isVisible && flowId) {
      fetchUserDatas();
    }
  }, [isVisible, flowId]);

  const fetchUserDatas = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apis.getUserDatasForFlow(flowId);
      if (response && response.success && Array.isArray(response.data)) {
        setUserDatas(response.data);
      } else {
        console.warn('Invalid user_datas response format');
        setUserDatas([]);
      }
    } catch (error) {
      console.error('Error fetching user_datas:', error);
      setUserDatas([]);
    } finally {
      setLoading(false);
    }
  }, [flowId]);

  const filteredUserDatas = userDatas.filter(data =>
    data.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    data.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelect = useCallback((userDataId: string) => {
    onSelect(userDataId);
    onClose();
  }, [onSelect, onClose]);

  return (
    <Modal
      visible={isVisible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={{
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20
      }}>
        <View style={{
          width: '100%',
          maxWidth: 600,
          maxHeight: '80%',
          backgroundColor: colors.background,
          borderRadius: 16,
          overflow: 'hidden'
        }}>
          {/* Header */}
          <View style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 20,
            borderBottomWidth: 1,
            borderBottomColor: colors.border
          }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
              <MaterialCommunityIcons name="database-search" size={24} color={colors.accent} />
              <Text style={{ fontSize: 18, fontWeight: '700', color: colors.text }}>
                Select User Data Schema
              </Text>
            </View>
            <Pressable onPress={onClose} style={{ padding: 4 }}>
              <MaterialIcons name="close" size={24} color={colors.secondaryText} />
            </Pressable>
          </View>

          {/* Search */}
          <View style={{ padding: 16, borderBottomWidth: 1, borderBottomColor: colors.border }}>
            <View style={{
              flexDirection: 'row',
              alignItems: 'center',
              backgroundColor: colors.surface,
              borderRadius: 8,
              paddingHorizontal: 12,
              paddingVertical: 10,
              gap: 8
            }}>
              <MaterialIcons name="search" size={20} color={colors.secondaryText} />
              <TextInput
                value={searchQuery}
                onChangeText={setSearchQuery}
                placeholder="Search user data schemas..."
                placeholderTextColor={colors.secondaryText}
                style={{
                  flex: 1,
                  fontSize: 14,
                  color: colors.text
                }}
              />
            </View>
          </View>

          {/* User Datas List */}
          <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 16, gap: 12 }}>
            {loading ? (
              <View style={{ padding: 40, alignItems: 'center' }}>
                <ActivityIndicator size="large" color={colors.accent} />
                <Text style={{ marginTop: 12, fontSize: 14, color: colors.secondaryText }}>
                  Loading schemas...
                </Text>
              </View>
            ) : filteredUserDatas.length === 0 ? (
              <View style={{ padding: 40, alignItems: 'center' }}>
                <MaterialCommunityIcons name="database-off" size={48} color={colors.secondaryText} />
                <Text style={{ marginTop: 12, fontSize: 14, color: colors.secondaryText, textAlign: 'center' }}>
                  {searchQuery ? 'No schemas match your search' : 'No user data schemas available'}
                </Text>
              </View>
            ) : (
              filteredUserDatas.map(data => (
                <Pressable
                  key={data._id}
                  onPress={() => handleSelect(data._id)}
                  style={{
                    backgroundColor: colors.surface,
                    padding: 16,
                    borderRadius: 12,
                    borderWidth: 1,
                    borderColor: colors.border
                  }}
                >
                  <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <View style={{ flex: 1 }}>
                      <Text style={{ fontSize: 16, fontWeight: '600', color: colors.text, marginBottom: 4 }}>
                        {data.title}
                      </Text>
                      {data.description && (
                        <Text style={{ fontSize: 13, color: colors.secondaryText, marginBottom: 8 }}>
                          {data.description}
                        </Text>
                      )}
                      <XStack gap={8} flexWrap="wrap">
                        <View style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 4,
                          backgroundColor: theme ? '#e0e7ff' : '#312e81',
                          paddingHorizontal: 8,
                          paddingVertical: 4,
                          borderRadius: 6
                        }}>
                          <MaterialCommunityIcons name="form-select" size={12} color={theme ? '#4f46e5' : '#a5b4fc'} />
                          <Text style={{ fontSize: 11, fontWeight: '600', color: theme ? '#4f46e5' : '#a5b4fc' }}>
                            {data.fieldCount} fields
                          </Text>
                        </View>
                        {data.tags?.map(tag => (
                          <View
                            key={tag}
                            style={{
                              backgroundColor: theme ? '#f1f5f9' : '#334155',
                              paddingHorizontal: 8,
                              paddingVertical: 4,
                              borderRadius: 6
                            }}
                          >
                            <Text style={{ fontSize: 11, color: colors.secondaryText }}>
                              {tag}
                            </Text>
                          </View>
                        ))}
                      </XStack>
                    </View>
                    <MaterialIcons name="chevron-right" size={24} color={colors.secondaryText} />
                  </View>
                </Pressable>
              ))
            )}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
};

export default UserDataSelector;

